import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import OfflineBanner from "./components/common/OfflineBanner";
import DocumentTitle from "./components/DocumentTitle";
import ProtectedRoute from "./components/common/Auth/ProtectedRoute";
import ApplicationAccessGuard from "./components/common/Auth/ApplicationAccessGuard";
import AnalyticsRoute from "./components/common/AnalyticsRoute";

// Citizenship Routes
import ReimbursmentLandingPage from "./pages/CitizenshipReimbursement/LandingPage/ReimbursmentLandingPage";
import ApplyTab from "./pages/CitizenshipReimbursement/Apply/Apply";
import UploadDocument from "./pages/CitizenshipReimbursement/UploadDocument/UploadDocument";
import ViewDetailsTabList from "./pages/CitizenshipReimbursement/UploadDocument/ViewDetailsTabList";
import ManagerPortal from "./pages/CitizenshipReimbursement/ManagerPortal/ManagerPortal";
import ManageDetails from "./pages/CitizenshipReimbursement/ManagerPortal/ManageDetails";

// Tuition Routes
import TuitionLandingPage from "./pages/TuitionReimbursement/LandingPage/TuitionLandingPage";
import TuitionApply from "./pages/TuitionReimbursement/Apply";
import TuitionUploadDocument from "./pages/TuitionReimbursement/UploadDocument/TuitionUploadDocument";
import TuitionManagerPortal from "./pages/TuitionReimbursement/ManagerPortal/TuitionManagerPortal";
import TuitionManagerDetails from "./pages/TuitionReimbursement/ManagerPortal/TuitionManagerDetails";
import OverViewTabList from "./pages/TuitionReimbursement/Overview/OverviewTabsList";
import TuitionViewDetailsTabList from "./pages/TuitionReimbursement/UploadDocument/TuitionViewDetailsTabList";

// Admin Routes
import AdminLandingPage from "./pages/CitizenshipReimbursement/Admin/Citizenship/AdminLandingPage";
import CitizenshipContainer from "./pages/CitizenshipReimbursement/Admin/Citizenship/CitizenshipContainer";
import TuitionContainer from "./pages/TuitionReimbursement/Admin/Tuition/TuitionContainer";
import ManageCannedResponses from "./pages/TuitionReimbursement/Admin/Tuition/ManageCannedResponses";
import TuitionErrorCorrection from "./pages/TuitionReimbursement/Admin/Tuition/AdministerSystem/TuitionErrorCorrection";

// Auth Routes
import AuthCallback from "./components/common/Auth/CallBack";
import Login from "./components/common/Auth/Login";
import AdminRoute from "./components/common/Auth/AdminRoute";

// Store
import { useAppSelector } from "./store/hooks";
import AdministerSystem from "./pages/TuitionReimbursement/Admin/Tuition/AdministerSystem/AdministerSystem";

const App: React.FC = () => {
  const [overlayVisible, setOverlayVisible] = useState(false);
  const { isAdmin } = useAppSelector((state) => state.profile);

  useEffect(() => {
    const onOpen = () => setOverlayVisible(true);
    const onClose = () => setOverlayVisible(false);
    globalThis.addEventListener("app-modal-open", onOpen as EventListener);
    globalThis.addEventListener("app-modal-close", onClose as EventListener);
    return () => {
      globalThis.removeEventListener("app-modal-open", onOpen as EventListener);
      globalThis.removeEventListener(
        "app-modal-close",
        onClose as EventListener,
      );
    };
  }, []);

  console.log("isAdmin:", isAdmin);

  return (
    <>
      <DocumentTitle />
      {/* App-level overlay to cover header + body when modal is open */}
      <div
        aria-hidden={!overlayVisible}
        className={`app-overlay ${
          overlayVisible ? "app-overlay--visible" : ""
        }`}
      />
      <Routes>
        {/* Public routes - no protection */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected routes - all app routes wrapped with layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className="app">
                <Header />
                <OfflineBanner />
                <main className="flex-1">
                  <ApplicationAccessGuard>
                    <Routes>
                    {/* Redirect root */}
                    <Route
                      path="/"
                      element={
                        isAdmin ? (
                          <Navigate to="/admin" replace />
                        ) : (
                          <Navigate to="/citizenship" replace />
                        )
                      }
                    />

                    {/* ================= CITIZENSHIP USER ROUTES ================= */}

                    {/* HOME */}
                    <Route
                      path="/citizenship"
                      element={
                        <AnalyticsRoute pageType="citizenship-home">
                          <ReimbursmentLandingPage />
                        </AnalyticsRoute>
                      }
                    />

                    {/* APPLY */}
                    <Route
                      path="/citizenship/apply"
                      element={
                        <AnalyticsRoute
                          pageType="citizenship-apply"
                          currentStep={1}
                        >
                          <ApplyTab
                            empId={""}
                            empNuid={""}
                            altPhone={""}
                            altEmail={""}
                          />
                        </AnalyticsRoute>
                      }
                    />

                    {/* UPLOAD DOCUMENT */}
                    <Route
                      path="/citizenship/upload-document"
                      element={
                        <AnalyticsRoute pageType="citizenship-upload">
                          <UploadDocument />
                        </AnalyticsRoute>
                      }
                    />

                    {/* VIEW DETAILS */}
                    <Route
                      path="/citizenship/detailstabs/:id"
                      element={
                        <AnalyticsRoute pageType="citizenship-upload">
                          <ViewDetailsTabList />
                        </AnalyticsRoute>
                      }
                    />

                    {/* MANAGER PORTAL */}
                    <Route
                      path="/citizenship/manager-portal"
                      element={
                        <AnalyticsRoute pageType="citizenship-manager-portal">
                          <ManagerPortal />
                        </AnalyticsRoute>
                      }
                    />

                    {/* MANAGER DETAILS */}
                    <Route
                      path="/citizenship/manager-details/:id"
                      element={
                        <AnalyticsRoute pageType="citizenship-manager-details">
                          <ManageDetails />
                        </AnalyticsRoute>
                      }
                    />

                    {/* ================= TUITION USER ROUTES ================= */}

                    {/* HOME */}
                    <Route
                      path="/tuition"
                      element={
                        <AnalyticsRoute pageType="tuition-home">
                          <TuitionLandingPage />
                        </AnalyticsRoute>
                      }
                    />

                    {/* APPLY */}
                    <Route
                      path="/tuition/apply"
                      element={
                        <AnalyticsRoute
                          pageType="tuition-apply"
                          currentStep={1}
                        >
                          <TuitionApply mode="employee" />
                        </AnalyticsRoute>
                      }
                    />

                    {/* UPLOAD DOCUMENT */}
                    <Route
                      path="/tuition/upload-document"
                      element={
                        <AnalyticsRoute pageType="tuition-upload">
                          <TuitionUploadDocument />
                        </AnalyticsRoute>
                      }
                    />

                    {/* VIEW DETAILS */}
                    <Route
                      path="/tuition/detailstabs/:id"
                      element={
                        <AnalyticsRoute pageType="tuition-upload">
                          <TuitionViewDetailsTabList />
                        </AnalyticsRoute>
                      }
                    />

                    {/* MANAGER PORTAL */}
                    <Route
                      path="/tuition/manager-portal"
                      element={
                        <AnalyticsRoute pageType="tuition-manager-portal">
                          <TuitionManagerPortal />
                        </AnalyticsRoute>
                      }
                    />

                    {/* MANAGER DETAILS */}
                    <Route
                      path="/tuition/manager-details/:id"
                      element={
                        <AnalyticsRoute pageType="tuition-manager-details">
                          <TuitionManagerDetails />
                        </AnalyticsRoute>
                      }
                    />

                    {/* OVERVIEW */}
                    <Route
                      path="/tuition/overview"
                      element={
                        <AnalyticsRoute pageType="tuition-overview">
                          <OverViewTabList />
                        </AnalyticsRoute>
                      }
                    />

                    {/* ================= ADMIN ROUTES ================= */}
                    <Route element={<AdminRoute />}>
                      {/* ADMIN HOME */}
                      <Route
                        path="/admin"
                        element={
                          <AnalyticsRoute pageType="admin-citizenship">
                            <AdminLandingPage />
                          </AnalyticsRoute>
                        }
                      />

                      {/* CITIZENSHIP ADMIN */}
                      <Route
                        path="/admin/citizenship"
                        element={
                          <AnalyticsRoute pageType="admin-citizenship">
                            <CitizenshipContainer />
                          </AnalyticsRoute>
                        }
                      />

                      {/* TUITION ADMIN */}
                      <Route
                        path="/admin/tuition"
                        element={
                          <AnalyticsRoute pageType="admin-tuition">
                            <TuitionContainer />
                          </AnalyticsRoute>
                        }
                      />

                      {/* CREATE APPLICATION (ADMIN MODE) */}
                      <Route
                        path="/admin/tuition/create-application"
                        element={
                          <AnalyticsRoute
                            pageType="tuition-apply"
                            currentStep={1}
                          >
                            <TuitionApply mode="admin" />
                          </AnalyticsRoute>
                        }
                      />

                      {/* CANNED RESPONSES */}
                      <Route
                        path="/tuition/admin/canned-responses"
                        element={
                          <AnalyticsRoute pageType="admin-tuition">
                            <ManageCannedResponses />
                          </AnalyticsRoute>
                        }
                      />

                      {/* ADMINISTER SYSTEM */}
                      <Route
                        path="/admin/administer"
                        element={
                          <AnalyticsRoute pageType="admin-tuition">
                            <AdministerSystem />
                          </AnalyticsRoute>
                        }
                      />

                      {/* ERROR CORRECTION */}
                      <Route
                        path="/admin/tuition/errors-to-correct"
                        element={
                          <AnalyticsRoute pageType="admin-tuition">
                            <TuitionErrorCorrection />
                          </AnalyticsRoute>
                        }
                      />
                    </Route>

                    {/* 404 NOT FOUND */}
                    <Route
                      path="*"
                      element={
                        <div className="min-h-screen text-center!">
                          <b>404 Not Found</b>
                        </div>
                      }
                    />
                    </Routes>
                  </ApplicationAccessGuard>
                </main>
                <Footer />
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

export default App;
